import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useEffect, useState } from 'react';
import { createEventAPI } from '../../api/events';

import { CreateEventPayload, OptionType } from '../../utils/types';
import { scrollAndFocusFirstErrorField } from '../../utils/helpers';
import { useTranslation } from '../../hooks/translations/TranslationProvider';

interface CreateEventFormValues {
    name: string;
    type: string;
    place: string;
    address: string;
    mapsUrl: string;
    ownerNotes: string;
    totalPrice: number;
    date: Date | null;
    duration: number;
    isPrivate: boolean;
    maxUsers: number;
}

const defaultFormValues = {
    name: '',
    type: '',
    place: '',
    address: '',
    mapsUrl: '',
    totalPrice: 0,
    date: null,
    duration: 0,
    isPrivate: false,
    maxUsers: 0,
    ownerNotes: '',
};

interface Errors {
    name: string | null;
    type: string | null;
    place: string | null;
    totalPrice: string | null;
    date: string | null;
    duration: string | null;
    isPrivate: string | null;
    maxUsers: string | null;
}

type ErrorField = keyof Errors;

const fieldSelectorByName: Partial<
    Record<keyof CreateEventFormValues, string>
> = {
    name: '[name="name"]',
    type: '[name="type"]',
    place: '[name="place"]',
    totalPrice: '[name="totalPrice"]',
    date: '[name="date"], #date-picker-date input',
    duration: '[name="duration"]',
    maxUsers: '[name="maxUsers"]',
};

export const useCreateEvent = () => {
    const navigate = useLocalizedNavigate();

    const [formValues, setFormValues] = useState<CreateEventFormValues>({
        ...defaultFormValues,
    });
    const [errors, setErrors] = useState<Errors>({
        name: null,
        type: null,
        place: null,
        totalPrice: null,
        date: null,
        duration: null,
        isPrivate: null,
        maxUsers: null,
    });
    const [serverError, setServerError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        setErrors({
            name: null,
            type: null,
            place: null,
            totalPrice: null,
            date: null,
            duration: null,
            isPrivate: null,
            maxUsers: null,
        });
    }, [formValues]);

    const inputChangeHandler = (
        name: string,
        value: string | boolean | number
    ) => {
        console.log(name);

        setFormValues({
            ...formValues,
            [name]: value,
        });
    };

    const onRadioChange = (selectedOption: string) => {
        setFormValues({
            ...formValues,
            isPrivate: selectedOption === 'private',
        });
    };

    const onEventTypeChange = (name: string, result: OptionType) => {
        setFormValues({
            ...formValues,
            [name]: result.id,
        });
    };

    const onDateChange = (newDate: Date) => {
        setFormValues({
            ...formValues,
            date: newDate,
        });
    };

    const onSave = async (event: React.FormEvent) => {
        event.preventDefault();

        let hasErrors = false;
        const formErrors = { ...errors };
        let key: keyof CreateEventFormValues;
        let firstErrorKey: ErrorField | null = null;

        const isErrorField = (
            field: keyof CreateEventFormValues
        ): field is ErrorField =>
            [
                'name',
                'type',
                'place',
                'totalPrice',
                'date',
                'duration',
                'isPrivate',
                'maxUsers',
            ].includes(field);

        const setFieldError = (field: ErrorField, errorMessage: string) => {
            hasErrors = true;
            formErrors[field] = errorMessage;
            if (!firstErrorKey) {
                firstErrorKey = field;
            }
        };

        for (key in formValues) {
            if (
                (formValues[key] === null || formValues[key] === '') &&
                key !== 'mapsUrl' &&
                key !== 'ownerNotes' &&
                isErrorField(key)
            ) {
                setFieldError(key, t('errors.requiredField'));
            }

            if (
                (key === 'maxUsers' || key === 'duration') &&
                formValues[key] === 0
            ) {
                setFieldError(key as ErrorField, t('errors.requiredField'));
            }

            if (key === 'maxUsers' && Number(formValues[key]) % 2 !== 0) {
                setFieldError(key as ErrorField, 'Must be an even number');
            }

            // if (
            //     (key === 'duration' || key === 'totalPrice') &&
            //     Number(formValues[key]) % 2 !== 0
            // ) {
            //     hasErrors = true;
            //     formErrors[key] = `Must be an even number`;
            // }
        }

        if (hasErrors) {
            setErrors({
                ...formErrors,
            });

            scrollAndFocusFirstErrorField(firstErrorKey, fieldSelectorByName);
            return;
        }
        try {
            setIsLoading(true);

            const createEventPayload: CreateEventPayload = {
                ...formValues,
                maxUsers: Number(formValues.maxUsers),
                totalPrice: Number(formValues.totalPrice),
                duration: Number(formValues.duration),
                date: formValues.date?.toISOString()!,
            };
            const eventId = await createEventAPI(createEventPayload);

            navigate(`/events/${eventId}`);
        } catch (e) {
            setServerError(true);
        }
    };

    return {
        formValues,
        errors,
        inputChangeHandler,
        onEventTypeChange,
        onDateChange,
        onSave,
        onRadioChange,
        serverError,
        isLoading,
    };
};
